import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateTriviaQuizTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const shortTextId = uuidv4();
  const shortTextBlockId = uuidv4();
  const statement1Id = uuidv4();
  const statement1BlockId = uuidv4();
  const multipleChoice1Id = uuidv4();
  const multipleChoice1BlockId = uuidv4();
  const numberId = uuidv4();
  const numberBlockId = uuidv4();
  const multipleChoice2Id = uuidv4();
  const multipleChoice2BlockId = uuidv4();
  const multipleChoice3Id = uuidv4();
  const multipleChoice3BlockId = uuidv4();
  const multipleChoice4Id = uuidv4();
  const multipleChoice4BlockId = uuidv4();
  const multipleChoice5Id = uuidv4();
  const multipleChoice5BlockId = uuidv4();
  const statement2Id = uuidv4();
  const statement2BlockId = uuidv4();
  const multipleChoice6Id = uuidv4();
  const multipleChoice6BlockId = uuidv4();
  const multipleChoice7Id = uuidv4();
  const multipleChoice7BlockId = uuidv4();
  const multipleChoice8Id = uuidv4();
  const multipleChoice8BlockId = uuidv4();
  const multipleChoice9Id = uuidv4();
  const multipleChoice9BlockId = uuidv4();
  const multipleChoice10Id = uuidv4();
  const multipleChoice10BlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options - MC1 (mammal flight)
  const mc1Option1Id = uuidv4(); // Chickens
  const mc1Option2Id = uuidv4(); // Flying squirrels
  const mc1Option3Id = uuidv4(); // Bats (correct)
  const mc1Option4Id = uuidv4(); // Penguins

  // Generate new UUIDs for select options - MC2 (planet spins backwards)
  const mc2Option1Id = uuidv4(); // Mars
  const mc2Option2Id = uuidv4(); // Earth
  const mc2Option3Id = uuidv4(); // Venus (correct)
  const mc2Option4Id = uuidv4(); // Neptune

  // Generate new UUIDs for select options - MC3 (New Zealand capital)
  const mc3Option1Id = uuidv4(); // Auckland
  const mc3Option2Id = uuidv4(); // Christchurch
  const mc3Option3Id = uuidv4(); // Wellington (correct)
  const mc3Option4Id = uuidv4(); // Dunedin

  // Generate new UUIDs for select options - MC4 (longest TV show)
  const mc4Option1Id = uuidv4(); // Grey's Anatomy
  const mc4Option2Id = uuidv4(); // Law & Order
  const mc4Option3Id = uuidv4(); // The Simpsons (correct)
  const mc4Option4Id = uuidv4(); // Doctor Who

  // Generate new UUIDs for select options - MC5 (scientist with cat door)
  const mc5Option1Id = uuidv4(); // Albert Einstein
  const mc5Option2Id = uuidv4(); // Stephen Hawking
  const mc5Option3Id = uuidv4(); // Isaac Newton (correct)
  const mc5Option4Id = uuidv4(); // Nikola Tesla

  // Generate new UUIDs for select options - MC6 (China time zones)
  const mc6Option1Id = uuidv4(); // 3
  const mc6Option2Id = uuidv4(); // 7
  const mc6Option3Id = uuidv4(); // 1 (correct)
  const mc6Option4Id = uuidv4(); // 5

  // Generate new UUIDs for select options - MC7 (deepest ocean)
  const mc7Option1Id = uuidv4(); // Atlantic
  const mc7Option2Id = uuidv4(); // Indian
  const mc7Option3Id = uuidv4(); // Pacific (correct)
  const mc7Option4Id = uuidv4(); // Arctic

  // Generate new UUIDs for select options - MC8 (dingo native country)
  const mc8Option1Id = uuidv4(); // South Africa
  const mc8Option2Id = uuidv4(); // Thailand
  const mc8Option3Id = uuidv4(); // Australia (correct)
  const mc8Option4Id = uuidv4(); // India

  // Generate new UUIDs for select options - MC9 (smallest country)
  const mc9Option1Id = uuidv4(); // Monaco
  const mc9Option2Id = uuidv4(); // Liechtenstein
  const mc9Option3Id = uuidv4(); // Vatican City (correct)
  const mc9Option4Id = uuidv4(); // San Marino

  // Generate new UUIDs for select options - MC10 (chemical symbol K)
  const mc10Option1Id = uuidv4(); // Krypton
  const mc10Option2Id = uuidv4(); // Potassium (correct)
  const mc10Option3Id = uuidv4(); // Kalium
  const mc10Option4Id = uuidv4(); // Platinum

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  // Generate new UUIDs for logic items and conditions
  const mc5Logic1Id = uuidv4();
  const mc5Logic2Id = uuidv4();
  const statement2Logic1Id = uuidv4();

  return {
    ...themes.sunsetGold,
    "collectionId": collectionId,
    "name": "Trivia Quiz",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO-black.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "If you ace this quiz, you might actually be too powerful.",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "There are 12 questions and zero snack breaks… unless you bring your own carbs.",
        "thirdContent": "Let's get started",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "4823938",
          "link": "https://unsplash.com/photos/q_FTiHRCeJo/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8YW5pbWFsJTIwZ2xhc3Nlc3xlbnwwfHx8fDE3NjQ1NzM5NzV8MA?q=80&w=600",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/4823938/pictures/preview-0.jpeg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "SHORT_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": shortTextId,
        "blockId": shortTextBlockId,
        "content": "What's your name, brave human?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "A first name, nickname, or secret superhero identity works.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": statement1Id,
        "blockId": statement1BlockId,
        "content": `Okay, {{${shortTextBlockId}}}, let's see what your brain's been hoarding.`,
        "manditory": true,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Give me your best shot",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": multipleChoice1Id,
        "blockId": multipleChoice1BlockId,
        "content": "What's the only mammal capable of true flight?",
        "manditory": true,
        "order": 3,
        "selectOptions": [
          {
            "id": mc1Option1Id,
            "order": 0,
            "value": "Chickens"
          },
          {
            "id": mc1Option2Id,
            "order": 1,
            "value": "Flying squirrels"
          },
          {
            "id": mc1Option3Id,
            "order": 2,
            "value": "Bats"
          },
          {
            "id": mc1Option4Id,
            "order": 3,
            "value": "Penguins"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc1Option3Id]: 1,
          [mc1Option4Id]: null,
          [mc1Option1Id]: null,
          [mc1Option2Id]: null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "NUMBER",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": numberId,
        "blockId": numberBlockId,
        "content": "How many bones are in the adult human body?",
        "manditory": true,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice2Id,
        "blockId": multipleChoice2BlockId,
        "content": "Which of these planets spins backwards compared to the others?",
        "manditory": true,
        "order": 5,
        "selectOptions": [
          {
            "id": mc2Option1Id,
            "order": 0,
            "value": "Mars"
          },
          {
            "id": mc2Option2Id,
            "order": 1,
            "value": "Earth"
          },
          {
            "id": mc2Option3Id,
            "order": 2,
            "value": "Venus"
          },
          {
            "id": mc2Option4Id,
            "order": 3,
            "value": "Neptune"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc2Option1Id]: null,
          [mc2Option2Id]: null,
          [mc2Option3Id]: 1,
          [mc2Option4Id]: null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice3Id,
        "blockId": multipleChoice3BlockId,
        "content": "Which of these is the capital of New Zealand?",
        "manditory": true,
        "order": 6,
        "selectOptions": [
          {
            "id": mc3Option1Id,
            "order": 0,
            "value": "Auckland"
          },
          {
            "id": mc3Option2Id,
            "order": 1,
            "value": "Christchurch"
          },
          {
            "id": mc3Option3Id,
            "order": 2,
            "value": "Wellington"
          },
          {
            "id": mc3Option4Id,
            "order": 3,
            "value": "Dunedin"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc3Option4Id]: null,
          [mc3Option2Id]: null,
          [mc3Option3Id]: 1,
          [mc3Option1Id]: null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice4Id,
        "blockId": multipleChoice4BlockId,
        "content": "What is the longest-running scripted TV show in history?",
        "manditory": true,
        "order": 7,
        "selectOptions": [
          {
            "id": mc4Option1Id,
            "order": 0,
            "value": "Grey's Anatomy"
          },
          {
            "id": mc4Option2Id,
            "order": 1,
            "value": "Law & Order"
          },
          {
            "id": mc4Option3Id,
            "order": 2,
            "value": "The Simpsons"
          },
          {
            "id": mc4Option4Id,
            "order": 3,
            "value": "Doctor Who"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc4Option2Id]: null,
          [mc4Option1Id]: null,
          [mc4Option4Id]: null,
          [mc4Option3Id]: 1
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice5Id,
        "blockId": multipleChoice5BlockId,
        "content": "Which famous scientist had a cat door cut into his lab door?",
        "manditory": true,
        "order": 8,
        "selectOptions": [
          {
            "id": mc5Option1Id,
            "order": 0,
            "value": "Albert Einstein"
          },
          {
            "id": mc5Option2Id,
            "order": 1,
            "value": "Stephen Hawking"
          },
          {
            "id": mc5Option3Id,
            "order": 2,
            "value": "Isaac Newton"
          },
          {
            "id": mc5Option4Id,
            "order": 3,
            "value": "Nikola Tesla"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 6,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": "heart",
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc5Option3Id]: 1,
          [mc5Option1Id]: null,
          [mc5Option2Id]: null,
          [mc5Option4Id]: null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "horizontalAlignment": false,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": mc5Logic1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": statement2BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice5Id,
            "conditions": []
          },
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": mc5Logic2Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": statement2BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice5Id,
            "conditions": []
          }
        ]
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": statement2Id,
        "blockId": statement2BlockId,
        "content": `You're getting close to wrapping up this quiz, and you currently have @score/6 answers correct! Let's see if you can finish strong {{${shortTextBlockId}}}!`,
        "manditory": true,
        "order": 9,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Let's do this!",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "horizontalAlignment": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": statement2Logic1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": multipleChoice6BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": statement2Id,
            "conditions": []
          }
        ]
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice6Id,
        "blockId": multipleChoice6BlockId,
        "content": "How many time zones does China officially use?",
        "manditory": true,
        "order": 10,
        "selectOptions": [
          {
            "id": mc6Option1Id,
            "order": 0,
            "value": "3"
          },
          {
            "id": mc6Option2Id,
            "order": 1,
            "value": "7"
          },
          {
            "id": mc6Option3Id,
            "order": 2,
            "value": "1"
          },
          {
            "id": mc6Option4Id,
            "order": 3,
            "value": "5"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc6Option3Id]: 1,
          [mc6Option2Id]: null,
          [mc6Option1Id]: null,
          [mc6Option4Id]: null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice7Id,
        "blockId": multipleChoice7BlockId,
        "content": "Which ocean is the deepest?",
        "manditory": true,
        "order": 11,
        "selectOptions": [
          {
            "id": mc7Option1Id,
            "order": 0,
            "value": "Atlantic"
          },
          {
            "id": mc7Option2Id,
            "order": 1,
            "value": "Indian"
          },
          {
            "id": mc7Option3Id,
            "order": 2,
            "value": "Pacific"
          },
          {
            "id": mc7Option4Id,
            "order": 3,
            "value": "Arctic"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc7Option4Id]: null,
          [mc7Option2Id]: null,
          [mc7Option1Id]: null,
          [mc7Option3Id]: 1
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice8Id,
        "blockId": multipleChoice8BlockId,
        "content": "The dingo is native to which country?",
        "manditory": true,
        "order": 12,
        "selectOptions": [
          {
            "id": mc8Option1Id,
            "order": 0,
            "value": "South Africa"
          },
          {
            "id": mc8Option2Id,
            "order": 1,
            "value": "Thailand"
          },
          {
            "id": mc8Option3Id,
            "order": 2,
            "value": "Australia"
          },
          {
            "id": mc8Option4Id,
            "order": 3,
            "value": "India"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc8Option3Id]: 1,
          [mc8Option2Id]: null,
          [mc8Option1Id]: null,
          [mc8Option4Id]: null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice9Id,
        "blockId": multipleChoice9BlockId,
        "content": "What's the smallest country in the world by land area?",
        "manditory": true,
        "order": 13,
        "selectOptions": [
          {
            "id": mc9Option1Id,
            "order": 0,
            "value": "Monaco"
          },
          {
            "id": mc9Option2Id,
            "order": 1,
            "value": "Liechtenstein"
          },
          {
            "id": mc9Option3Id,
            "order": 2,
            "value": "Vatican City"
          },
          {
            "id": mc9Option4Id,
            "order": 3,
            "value": "San Marino"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc9Option1Id]: null,
          [mc9Option4Id]: null,
          [mc9Option3Id]: 1,
          [mc9Option2Id]: null
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice10Id,
        "blockId": multipleChoice10BlockId,
        "content": "Which chemical element has the symbol \"K\"?",
        "manditory": true,
        "order": 14,
        "selectOptions": [
          {
            "id": mc10Option1Id,
            "order": 0,
            "value": "Krypton"
          },
          {
            "id": mc10Option2Id,
            "order": 1,
            "value": "Potassium"
          },
          {
            "id": mc10Option3Id,
            "order": 2,
            "value": "Kalium"
          },
          {
            "id": mc10Option4Id,
            "order": 3,
            "value": "Platinum"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": {
          [mc10Option1Id]: null,
          [mc10Option3Id]: null,
          [mc10Option4Id]: null,
          [mc10Option2Id]: 1
        },
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": `You scored @score/11 answers correct{{${shortTextBlockId}}}!`,
        "manditory": false,
        "order": 15,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Whether that's impressive or concerning is entirely up to interpretation.",
        "thirdContent": "Try another quiz!",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": endScreenMediaId,
          "type": "image",
          "name": "4823938",
          "link": "https://unsplash.com/photos/geJrExiijOw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1M3x8Z2xhc3NlcyUyMGFuaW1hbCUyMHxlbnwwfHx8fDE3NjQ1NzQxMTJ8MA?q=80&w=600",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/4823938/pictures/preview-0.jpeg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      }
    ],
    "snippets": [],
    "metaTitle": "Trivia Quiz",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateTriviaQuizTemplate;
